import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Schedule,
  Block,
  ArrowForward,
  Login,
  Email,
  Person
} from '@mui/icons-material';
import ResetPwdService from '../service/ResetPwdService';

const TokenValidation = ({ onSuccess, onError, resetData }) => {
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [validationComplete, setValidationComplete] = useState(false);

  useEffect(() => {
    validateToken();
  }, []);

  const validateToken = async () => {
    
    if (!resetData.token) {
      setAlert({
        severity: 'error',
        message: 'Aucun token de réinitialisation trouvé dans l\'URL'
      });
      setLoading(false);
      setTimeout(() => onError(), 7000);
      return;
    }

    try {

      const data = await ResetPwdService.validateResetToken(resetData?.token)
      
      if (data?.success) {
        setUserInfo({
          email: data.userEmail,
          name: data.userName
        });
        setAlert({
          severity: 'success',
          message: 'Lien valide ! Vous pouvez continuer.'
        });
        setValidationComplete(true);
      } else {
        setAlert({
          severity: 'error',
          message: data.message || 'Token invalide ou expiré'
        });
        setTimeout(() => onError(), 3000);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setAlert({
        severity: 'error',
        message: 'Erreur de connexion. Veuillez réessayer.'
      });
      setTimeout(() => onError(), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    onSuccess(userInfo);
  };

  const handleCancel = () => {
    window.location.href = '/web/bicici'; // Ajustez selon votre configuration
  };

  if (loading) {
    return (
      <Box>
        {/* En-tête */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom color="primary.main">
            🔗 Validation du lien
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Étape 3/4 : Vérification du lien de réinitialisation
          </Typography>
        </Box>

        {/* Loading */}
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Vérification en cours...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Validation de votre lien de réinitialisation
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary.main">
          🔗 Validation du lien
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Étape 3/4 : Vérification du lien de réinitialisation
        </Typography>
      </Box>

      {/* Alerte */}
      {alert && (
        <Alert
          severity={alert.severity}
          sx={{ mb: 3 }}
          icon={alert.severity === 'success' ? <CheckCircle /> : <Error />}
        >
          {alert.message}
        </Alert>
      )}

      {validationComplete && userInfo ? (
        <Fade in={validationComplete}>
          <Box>
            {/* Informations utilisateur */}
            <Paper
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: 'success.50',
                border: '2px solid',
                borderColor: 'success.200'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckCircle />
                </Avatar>
                <Typography variant="h6" color="success.main">
                  ✅ Lien valide !
                </Typography>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Person color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Nom"
                    secondary={userInfo.name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Email color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={userInfo.email}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Instructions */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
              <Typography variant="body1" gutterBottom>
                <strong>Votre lien de réinitialisation est valide.</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Vous pouvez maintenant procéder à la création de votre nouveau mot de passe.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule color="warning" fontSize="small" />
                <Typography variant="body2" color="warning.main">
                  <em>Ce lien expire dans 30 minutes pour votre sécurité.</em>
                </Typography>
              </Box>
            </Paper>

            {/* Boutons d'action */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleContinue}
                endIcon={<ArrowForward />}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #2e7d32 100%)',
                  },
                }}
              >
                Continuer vers la réinitialisation
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleCancel}
                startIcon={<Login />}
                sx={{ py: 1.5 }}
              >
                Annuler et retourner à la connexion
              </Button>
            </Box>
          </Box>
        </Fade>
      ) : (
        <Fade in={!validationComplete}>
          <Box>
            {/* Erreur de validation */}
            <Paper
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: 'error.50',
                border: '2px solid',
                borderColor: 'error.200'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                  <Block />
                </Avatar>
                <Typography variant="h6" color="error.main">
                  Lien invalide ou expiré
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Le lien de réinitialisation n'est pas valide ou a expiré.
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Causes possibles :</strong>
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Schedule color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Le lien a expiré (validité : 30 minutes)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Le lien a déjà été utilisé"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Error color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Le lien est incorrect ou incomplet"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Paper>

            {/* Boutons d'action pour erreur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={onError}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                }}
              >
                Demander un nouveau lien
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleCancel}
                startIcon={<Login />}
                sx={{ py: 1.5 }}
              >
                Retourner à la connexion
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Information */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          💡 Vérifiez votre email pour cliquer sur le lien de réinitialisation
        </Typography>
      </Box>
    </Box>
  );
};

export default TokenValidation;